import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { FileSpreadsheet, CheckCircle, AlertCircle, Clock, Play, Pause } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend } from 'recharts';

const PublicPortugueseAssessment = () => {
  const { token } = useParams();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);

  const progress = ((currentQuestionIndex + 1) / (questions?.length || 1)) * 100;

  useEffect(() => {
    loadLinkData();
  }, [token]);

  useEffect(() => {
    let interval;
    if (timerRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerRunning(false);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, timeRemaining]);

  const loadLinkData = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/portuguese/public/${token}`);
      
      if (response.data.link.completed) {
        setError('Esta avaliação já foi completada.');
        setLoading(false);
        return;
      }
      
      setLinkData(response.data.link);
      setQuestions(response.data.questions);
      
      const initialAnswers = {};
      response.data.questions.forEach(q => {
        initialAnswers[q.id] = null;
      });
      setAnswers(initialAnswers);
      
      setStartTime(Date.now());
      setLoading(false);
      setTimerRunning(true);
    } catch (error) {
      console.error('Error loading link:', error);
      if (error.response?.status === 404) {
        setError('Link não encontrado ou inválido.');
      } else if (error.response?.status === 410) {
        setError(error.response.data.detail || 'Link expirado ou já utilizado.');
      } else {
        setError('Erro ao carregar informações da avaliação.');
      }
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleSubmit = async () => {
    const answeredCount = Object.values(answers).filter(a => a !== null).length;
    
    if (answeredCount < questions.length && !window.confirm(
      `Você respondeu ${answeredCount} de ${questions.length} questões. Deseja realmente enviar?`
    )) {
      return;
    }

    try {
      setSubmitting(true);
      setTimerRunning(false);
      
      const timeMinutes = startTime ? Math.round((Date.now() - startTime) / 1000 / 60) : null;
      
      const formattedAnswers = Object.entries(answers)
        .filter(([_, value]) => value !== null)
        .map(([questionId, selectedOption]) => ({
          question_id: parseInt(questionId),
          selected_option: selectedOption
        }));

      const response = await axios.post(
        `${backendUrl}/api/portuguese/public/${token}/complete`,
        {
          answers: formattedAnswers,
          time_minutes: timeMinutes
        }
      );

      setResult(response.data);
      setCompleted(true);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('Erro ao enviar avaliação. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLevelColor = (level) => {
    const colors = {
      'Básico': '#ef4444',
      'Intermediário': '#eab308',
      'Avançado': '#22c55e'
    };
    return colors[level] || '#6b7280';
  };

  const getRadarData = () => {
    if (!result) return [];
    return [
      { subject: 'Gramática', score: result.gramatica_score, fullMark: 100 },
      { subject: 'Ortografia', score: result.ortografia_score, fullMark: 100 },
      { subject: 'Interpretação', score: result.interpretacao_score, fullMark: 100 },
      { subject: 'Redação', score: result.redacao_score, fullMark: 100 },
      { subject: 'Vocabulário', score: result.vocabulario_score, fullMark: 100 }
    ];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-blue-600 animate-pulse" />
              <p className="text-lg">Carregando avaliação...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-600" />
              <h2 className="text-xl font-bold mb-2">Erro</h2>
              <p className="text-gray-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed && result) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <div className="text-center">
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-blue-600" />
                <CardTitle className="text-2xl">Avaliação Concluída!</CardTitle>
                <p className="text-gray-600 mt-2">
                  Obrigado por completar a avaliação, {linkData?.participant_name}
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Score Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-blue-50">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Nível Nível</p>
                      <div 
                        className="text-5xl font-bold mb-2"
                        style={{ color: getNívelColor(result.detected_level) }}
                      >
                        {result.detected_level}
                      </div>
                      <p className="text-lg font-semibold">{result.detected_level}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Score Geral</p>
                      <div className="text-5xl font-bold text-blue-600 mb-2">
                        {result.score_geral}%
                      </div>
                      <p className="text-sm text-gray-500">
                        Aproveitamento geral
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Radar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Desempenho por Categoria</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={getRadarData()}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="subject" />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.6}
                      />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Category Breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-2 text-center">Gramática</h3>
                    <p className="text-3xl font-bold text-blue-600 text-center">
                      {result.gramatica_score}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-2 text-center">Ortografia</h3>
                    <p className="text-3xl font-bold text-blue-600 text-center">
                      {result.ortografia_score}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-2 text-center">Interpretação</h3>
                    <p className="text-3xl font-bold text-purple-600 text-center">
                      {result.interpretacao_score}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-2 text-center">Redação</h3>
                    <p className="text-3xl font-bold text-orange-600 text-center">
                      {result.redacao_score}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-2 text-center">Vocabulário</h3>
                    <p className="text-3xl font-bold text-teal-600 text-center">
                      {result.vocabulario_score}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <p className="text-sm text-blue-900">
                  Seus resultados foram salvos e enviados para o responsável pela avaliação.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-8 w-8 text-blue-600" />
                <div>
                  <CardTitle>Avaliação de Portuguese</CardTitle>
                  <p className="text-sm text-gray-600">
                    Olá, {linkData?.participant_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span className={`font-mono text-lg ${timeRemaining < 300 ? 'text-red-600 font-bold' : ''}`}>
                  {formatTime(timeRemaining)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setTimerRunning(!timerRunning)}
                >
                  {timerRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Progress Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex justify-between text-sm mb-2">
              <span>Questão {currentQuestionIndex + 1} de {questions.length}</span>
              <span>{Object.values(answers).filter(a => a !== null).length} respondidas</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Question Card */}
        <Card>
          <CardHeader>
            <CardTitle>
              {questions[currentQuestionIndex]?.question}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              Categoria: {questions[currentQuestionIndex]?.category}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Options */}
            <div className="space-y-3">
              {questions[currentQuestionIndex] && 
                Object.entries(questions[currentQuestionIndex].options).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => handleAnswer(questions[currentQuestionIndex].id, key)}
                    className={`w-full p-4 text-left border-2 rounded-lg transition-all ${
                      answers[questions[currentQuestionIndex].id] === key
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <span className="font-semibold mr-2">{key.toUpperCase()})</span>
                    {value}
                  </button>
                ))
              }
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                disabled={currentQuestionIndex === 0}
              >
                Anterior
              </Button>

              {currentQuestionIndex < questions.length - 1 ? (
                <Button
                  onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                >
                  Próxima
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submitting ? 'Enviando...' : 'Finalizar Avaliação'}
                </Button>
              )}
            </div>

            {/* Question Navigation Grid */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-2">Navegação Rápida:</p>
              <div className="grid grid-cols-10 gap-2">
                {questions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`p-2 text-sm rounded ${
                      idx === currentQuestionIndex
                        ? 'bg-blue-600 text-white'
                        : answers[q.id] !== null
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicPortugueseAssessment;
